import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import { Box, type BoxProps, Spinner, Text, Center } from "@chakra-ui/react";
import { TenantContext } from "../shared/contexts/tenantContext";
import type { Position, Workplace } from "../shared/types";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

interface GoogleMapProps extends BoxProps {
  setSelectedWorkplace?: (workplace: Workplace) => void;
  selectedWorkplace?: Workplace | undefined;
  workplaces: Workplace[];
  zoom?: number;
  extraNode?: React.ReactNode;
}

export const GoogleMap: React.FC<GoogleMapProps> = ({
  setSelectedWorkplace,
  selectedWorkplace,
  workplaces,
  zoom = 14,
  extraNode,
  ...boxProps
}) => {
  const render = (status: Status) => {
    if (status === Status.FAILURE) {
      return <Text>Could not load Google Maps</Text>;
    }
    return (
      <Center height="100%">
        <Spinner />
      </Center>
    );
  };

  return (
    <Box {...boxProps}>
      <Wrapper apiKey={process.env.googleMapsApiKey || ""} render={render}>
        <MapLoader
          setSelectedWorkplace={setSelectedWorkplace}
          selectedWorkplace={selectedWorkplace}
          workplaces={workplaces}
          center={{
            latitude: workplaces[0].latitude,
            longitude: workplaces[0].longitude,
          }}
          zoom={zoom}
          extraNode={extraNode}
          width={boxProps.width || "full"}
          height={boxProps.height || "100vh"}
        />
      </Wrapper>
    </Box>
  );
};

interface MapLoaderProps extends BoxProps {
  setSelectedWorkplace?: (workplace: Workplace) => void;
  selectedWorkplace?: Workplace | undefined;
  workplaces: Workplace[];
  center: Position;
  zoom: number;
  extraNode?: React.ReactNode;
}

const MapLoader: React.FC<MapLoaderProps> = ({
  setSelectedWorkplace,
  selectedWorkplace,
  workplaces,
  center,
  zoom,
  extraNode,
  ...boxProps
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const tenantConfiguration = React.useContext(TenantContext);

  const markers = workplaces.map((workplace: Workplace) => {
    const marker = new google.maps.Marker({
      icon: `/${tenantConfiguration.name}/map_marker_icon.svg`,
      position: {
        lat: workplace.latitude as number,
        lng: workplace.longitude as number,
      },
    });

    if (setSelectedWorkplace) {
      google.maps.event.addListener(marker, "click", () => {
        setSelectedWorkplace(workplace);
      });
    }

    return marker;
  });

  useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        mapId: tenantConfiguration.googleMapId,
        center: {
          lat: center.latitude as number,
          lng: center.longitude as number,
        },
        mapTypeControlOptions: {
          mapTypeIds: [],
        },
        zoom,
        streetViewControl: false,
      });
      setMap(newMap);

      const renderer = {
        render: ({
          count,
          position,
        }: {
          count: number;
          position: google.maps.LatLng;
        }) =>
          new google.maps.Marker({
            icon: `/${tenantConfiguration.name}/marker_cluster_icon.svg`,
            label: { text: String(count), color: "white", fontSize: "10px" },
            position,
          }),
      };

      // add a marker clusterer to manage the markers:
      new MarkerClusterer({
        markers,
        map: newMap,
        renderer,
      });

      if (markers.length > 1) {
        const bounds = new google.maps.LatLngBounds();
        for (let i = 0; i < markers.length; i++) {
          bounds.extend((markers[i] as any).position);
        }
        newMap.fitBounds(bounds);
      }
    }
  }, [center, map, markers, tenantConfiguration, zoom]);

  useEffect(() => {
    if (selectedWorkplace?.latitude && selectedWorkplace.longitude) {
      const mapCenter = {
        latitude: selectedWorkplace.latitude,
        longitude: selectedWorkplace.longitude,
      };

      map?.panTo(
        new google.maps.LatLng(mapCenter.latitude, mapCenter.longitude)
      );
    }
  }, [map, selectedWorkplace?.latitude, selectedWorkplace?.longitude]);

  return (
    <Box position="relative">
      <Box ref={ref} id="map" {...boxProps} />
      {extraNode && <>{extraNode}</>}
    </Box>
  );
};
